import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  LocalShipping as TradeIcon,
  Security as SecurityIcon,
  Assessment as AssessmentIcon,
  AccountBalance as AccountBalanceIcon,
  Public as PublicIcon,
  Speed as SpeedIcon,
  IntegrationInstructions as IntegrationIcon,
  Gavel as GavelIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const ACESystemDetail: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/admin/pi-planning');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to PI Planning Board"
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: '#003366', fontWeight: 'bold' }}>
            Automated Commercial Environment (ACE)
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            CBP Trade Facilitation and Compliance Management System
          </Typography>
        </Box>
      </Box>

      {/* System Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            System Overview
          </Typography>
          <Typography variant="body1" paragraph>
            The Automated Commercial Environment (ACE) is U.S. Customs and Border Protection's comprehensive electronic 
            system for processing imports and exports. As the backbone of U.S. trade facilitation, ACE modernizes and 
            streamlines the commercial import/export process, enabling the trade community to electronically submit required 
            documentation, track cargo, and ensure compliance with customs regulations and Partner Government Agency (PGA) requirements.
          </Typography>
        </CardContent>
      </Card>

      {/* 5Ws Summary */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        System Overview: The 5Ws
      </Typography>

      <Card sx={{ mb: 4 }}>
        <TableContainer>
          <Table aria-label="ACE System 5Ws Overview">
            <TableBody>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ width: '15%', bgcolor: '#D32F2F', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="What is ACE"
                >
                  What
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    A comprehensive <strong>commercial trade processing system</strong> that automates import/export documentation, 
                    cargo tracking, duty collection, and compliance enforcement. ACE serves as the <strong>single window</strong> for 
                    submitting data to CBP and 47 Partner Government Agencies (PGAs), replacing legacy paper-based processes with 
                    electronic filing and real-time status updates.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#1976D2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Who uses ACE"
                >
                  Who
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Operated by <strong>CBP Office of Trade</strong> and used by the international trade community including 
                    <strong> importers, exporters, customs brokers, carriers, freight forwarders, and Partner Government Agencies</strong>. 
                    Over <strong>150,000 registered users</strong> across 28,000 companies process $2.5 trillion in annual trade through ACE.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#2E7D32', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="When was ACE deployed"
                >
                  When
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Development began in <strong>2001</strong> to modernize legacy Automated Commercial System (ACS). 
                    <strong> Full deployment completed in 2016</strong> after phased rollout. Continuously enhanced with 
                    <strong> Single Window (2016)</strong>, e-Manifest (2018), and <strong>Section 321 processing (2019)</strong>. 
                    Operates <strong>24/7/365</strong> processing millions of daily transactions.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#F57C00', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Where is ACE deployed"
                >
                  Where
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Cloud-based system accessible <strong>nationwide and internationally</strong> by registered trade community members. 
                    Used at <strong>all 328 U.S. ports of entry</strong> for cargo processing. Integrated with <strong>ACE Secure Data Portal</strong> 
                    for web-based access and <strong>ACE Reports</strong> for trade analytics. Supports <strong>international Single Window 
                    interoperability</strong> with foreign customs systems.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#7B1FA2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Why ACE exists"
                >
                  Why
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    To <strong>facilitate legitimate trade</strong> while ensuring compliance with U.S. laws and regulations. Reduces 
                    paperwork burden, accelerates cargo release times, improves targeting accuracy, and enables <strong>risk-based 
                    enforcement</strong>. Supports economic competitiveness by streamlining trade processes and providing <strong>single 
                    window access</strong> to all government agencies regulating imports/exports.
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Core Capabilities */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <TradeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Core System Capabilities
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #003366' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TradeIcon sx={{ fontSize: 40, color: '#003366', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#003366', fontWeight: 'bold' }}>
                  Import/Export Processing
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Comprehensive electronic filing and processing of commercial shipments.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Entry Summary Filing:</strong> Electronic submission of CBP Form 7501</Typography></li>
                <li><Typography variant="body2"><strong>Cargo Release:</strong> Automated release processing and notifications</Typography></li>
                <li><Typography variant="body2"><strong>In-Bond Processing:</strong> Transit and transportation entry management</Typography></li>
                <li><Typography variant="body2"><strong>Post-Release Actions:</strong> Amendments, protests, and reconciliation</Typography></li>
                <li><Typography variant="body2"><strong>Export Manifest:</strong> AES Direct filing integration</Typography></li>
                <li><Typography variant="body2"><strong>Section 321:</strong> De minimis shipment processing ($800 threshold)</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  Trade Compliance Management
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Enforcement of trade laws, regulations, and compliance programs.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>C-TPAT:</strong> Customs-Trade Partnership Against Terrorism enrollment</Typography></li>
                <li><Typography variant="body2"><strong>ISF Filing:</strong> Importer Security Filing (10+2) compliance</Typography></li>
                <li><Typography variant="body2"><strong>Trade Remedies:</strong> AD/CVD case management and enforcement</Typography></li>
                <li><Typography variant="body2"><strong>Forced Labor:</strong> UFLPA and WRO enforcement</Typography></li>
                <li><Typography variant="body2"><strong>IPR Protection:</strong> Intellectual property rights enforcement</Typography></li>
                <li><Typography variant="body2"><strong>Audits & Penalties:</strong> Compliance verification and enforcement actions</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AccountBalanceIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  Duty & Tariff Calculation
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Automated assessment and collection of duties, taxes, and fees.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>HTS Classification:</strong> Harmonized Tariff Schedule lookup and validation</Typography></li>
                <li><Typography variant="body2"><strong>Duty Calculation:</strong> Automated assessment based on classification and value</Typography></li>
                <li><Typography variant="body2"><strong>MPF/HMF:</strong> Merchandise Processing and Harbor Maintenance Fees</Typography></li>
                <li><Typography variant="body2"><strong>Trade Agreements:</strong> FTA preference and rules of origin verification</Typography></li>
                <li><Typography variant="body2"><strong>Drawback:</strong> Duty refund processing for re-exported goods</Typography></li>
                <li><Typography variant="body2"><strong>ACH Payments:</strong> Electronic payment processing and reconciliation</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <IntegrationIcon sx={{ fontSize: 40, color: '#F57C00', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                  Partner Government Agency (PGA) Integration
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Single Window for submitting data to 47 federal agencies regulating imports.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>FDA:</strong> Food, drug, and medical device import clearances</Typography></li>
                <li><Typography variant="body2"><strong>USDA/APHIS:</strong> Agricultural and plant health inspections</Typography></li>
                <li><Typography variant="body2"><strong>EPA:</strong> Environmental compliance and chemical imports</Typography></li>
                <li><Typography variant="body2"><strong>CPSC:</strong> Consumer product safety certifications</Typography></li>
                <li><Typography variant="body2"><strong>DOT/NHTSA:</strong> Vehicle and equipment safety standards</Typography></li>
                <li><Typography variant="body2"><strong>FWS/NMFS:</strong> Wildlife and fisheries import permits</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* System Performance */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <PublicIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        System Scale & Performance
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #003366' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#003366', fontWeight: 'bold' }}>
                $2.5T
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Annual Trade Value
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Processed through ACE
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                30M+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Annual Entry Summaries
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Import declarations filed
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                150K+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Registered Users
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Across 28,000 companies
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                99.9%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                System Availability
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                24/7/365 uptime
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Key Benefits */}
      <Card sx={{ mb: 4, bgcolor: '#e3f2fd', border: '2px solid #1976D2' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
            Trade Community Benefits
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Faster Cargo Release:</strong> 50% reduction in release times vs. paper</Typography></li>
                <li><Typography variant="body2"><strong>Reduced Costs:</strong> Elimination of paper filing and courier expenses</Typography></li>
                <li><Typography variant="body2"><strong>Real-Time Visibility:</strong> Track shipment status and CBP actions</Typography></li>
                <li><Typography variant="body2"><strong>Single Window:</strong> One submission to CBP and 47 PGAs</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Improved Accuracy:</strong> Automated validation reduces errors</Typography></li>
                <li><Typography variant="body2"><strong>Enhanced Security:</strong> Better targeting and risk assessment</Typography></li>
                <li><Typography variant="body2"><strong>Compliance Support:</strong> Built-in checks and guidance</Typography></li>
                <li><Typography variant="body2"><strong>Data Analytics:</strong> ACE Reports for business intelligence</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ACE Portals */}
      <Card sx={{ mb: 4, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            <GavelIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            ACE Access Portals
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Chip label="ACE Secure Data Portal" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Web-based interface for filing and queries</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Chip label="ABI (Automated Broker Interface)" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">EDI-based system-to-system integration</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Chip label="ACE Reports" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Trade data analytics and reporting</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Chip label="e-Manifest" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Advance cargo information filing</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Chip label="ACE Portal Accounts" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">User registration and management</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Chip label="Partner Government Agency (PGA) Message Set" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Single Window data submission</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ACESystemDetail;
